import {
  createDatabaseEntryFieldValuesSnapshot,
  type DatabaseEntryFieldValuesSnapshot
} from '@databases/ai-first-creation-serializer/database-entry-field-values-snapshot';
import type { AiFieldGenerationWriteContext } from '@databases/ai-first-creation-token-processing';
import { collabStateManager } from '@databases/collab-state-manager';
import type { Database } from '@databases/model/database';
import { isAiFieldType, type Field, FieldType } from '@databases/model/field';
import type { AiSummaryValue, Value } from '@databases/model/value';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { appStore } from '@databases/redux/store';
import {
  createConfluencePageConnectionResolver,
  createJiraIssueDetailsResolver
} from '@databases/rovo-bridge';
import type {
  AiSummaryFieldConfig,
  DatabaseAiFieldGenerationFingerprint
} from '@databases/types/database';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';

type ManualAiFieldRefreshScope =
  | { kind: 'all-entries' }
  | { kind: 'empty-target-field-entries' }
  | { kind: 'entry'; entryId: string }
  | { kind: 'entries'; entryIds: readonly string[] };

type PreparedAiFieldRefresh = {
  eligibleEntryIds: readonly string[];
  generationWriteContext: AiFieldGenerationWriteContext;
  requestContext: DatabaseEntryFieldValuesSnapshot;
};

type AiFieldRefreshPreparationAdapter = {
  targetFieldLabel: string;
  isTargetField: (field: Field) => boolean;
  isEmptyValue: (value: Value<FieldType> | undefined) => boolean;
  getGenerationFingerprint: (
    value: Value<FieldType> | undefined
  ) => DatabaseAiFieldGenerationFingerprint | undefined;
};

type CurrentAiFieldGenerationFingerprint = Extract<
  DatabaseAiFieldGenerationFingerprint,
  { fingerprintVersion: 2 }
>;

type EntryPreparation = {
  fingerprint: CurrentAiFieldGenerationFingerprint;
  targetValue: string;
};

type AiFieldGenerationEligibility = 'all-targets' | 'empty-or-stale';

const compareFieldIds = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

const createAiFieldSourceInput = (
  valuesByFieldId: ReadonlyArray<readonly [string, string]>
): string =>
  JSON.stringify(
    [...valuesByFieldId].sort(([left], [right]) => compareFieldIds(left, right))
  );

const createAiSummaryConfigurationInput = ({
  outputFormat
}: AiSummaryFieldConfig): string => JSON.stringify([['outputFormat', outputFormat]]);

const serializeTargetValue = (value: Value<FieldType> | undefined): string =>
  value === undefined ? 'missing' : JSON.stringify(value.toJSON());

const aiSummaryFieldRefreshPreparationAdapter: AiFieldRefreshPreparationAdapter = {
  targetFieldLabel: 'AI Summary',
  isTargetField: (field) => field.type === FieldType.AiSummary,
  isEmptyValue: (value) =>
    value === undefined || (value.type === FieldType.AiSummary && value.isEmpty),
  getGenerationFingerprint: (value) => {
    if (value?.type !== FieldType.AiSummary) {
      return undefined;
    }

    const metadata = (value as AiSummaryValue).value.metadata;
    return metadata?.lastUpdatedBy === 'user' ? undefined : metadata?.generation;
  }
};

const readViewSnapshot = (contentId: string, viewId: string) => {
  const state = appStore.getState();
  const database = selectDatabaseByContentIdRequired(state, { contentId });
  const view = database.views.find(({ id }) => id === viewId);
  if (!view) {
    throw new Error(`Database view ${viewId} not found`);
  }
  const databaseDraft = selectDatabaseDraftByContentId(state, {
    contentId,
    includeDeleted: false,
    viewSettings: view.settings
  });

  return { databaseDraft, state };
};

const getVisibleSourceFieldIds = (
  visibleFieldIds: readonly string[],
  fieldsById: Database['fieldsById']
): readonly string[] =>
  [...visibleFieldIds]
    .filter((fieldId) => {
      const field = fieldsById[fieldId];
      return field !== undefined && !isAiFieldType(field.type);
    })
    .sort(compareFieldIds);

const createSourceInputForEntry = (
  entryId: string,
  sourceFieldIds: readonly string[],
  entryFieldValuesSnapshot: DatabaseEntryFieldValuesSnapshot
): string =>
  createAiFieldSourceInput(
    sourceFieldIds.map(
      (fieldId) =>
        [
          fieldId,
          entryFieldValuesSnapshot.valuesByEntryId.get(entryId)?.get(fieldId) ?? ''
        ] as const
    )
  );

const createFingerprintHash = async (input: string): Promise<string> => {
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input)
  );
  let binaryDigest = '';
  for (const byte of new Uint8Array(digest)) {
    binaryDigest += String.fromCharCode(byte);
  }
  const base64 = globalThis.btoa(binaryDigest);

  return base64.replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
};

const getTargetEntryIds = (
  visibleEntryIds: readonly string[],
  database: Database,
  fieldId: string,
  scope: ManualAiFieldRefreshScope,
  adapter: AiFieldRefreshPreparationAdapter
): readonly string[] => {
  switch (scope.kind) {
    case 'all-entries':
      return visibleEntryIds;
    case 'empty-target-field-entries':
      return visibleEntryIds.filter((entryId) =>
        adapter.isEmptyValue(database.entriesById[entryId]?.getValue(fieldId))
      );
    case 'entry':
      return database.entriesById[scope.entryId] ? [scope.entryId] : [];
    case 'entries':
      return [...new Set(scope.entryIds)].filter(
        (entryId) => database.entriesById[entryId] !== undefined
      );
    default: {
      const unhandledScope: never = scope;
      throw new Error(`Unhandled manual AI field refresh scope: ${unhandledScope}`);
    }
  }
};

/**
 * Snapshots the initiating view's inputs and creates atomic-write guards for either
 * change-aware refreshes or unconditional generation.
 */
async function prepareAiFieldRun(
  contentId: string,
  fieldId: string,
  scope: ManualAiFieldRefreshScope,
  viewId: string,
  configurationInput: string,
  adapter: AiFieldRefreshPreparationAdapter,
  eligibility: AiFieldGenerationEligibility
): Promise<PreparedAiFieldRefresh> {
  const manager = collabStateManager.getCollabManager(contentId);
  if (!manager) {
    throw new Error('Database not found');
  }

  const field = manager.database.fieldsById[fieldId];
  if (!field || !adapter.isTargetField(field)) {
    throw new Error(`${adapter.targetFieldLabel} field ${fieldId} not found`);
  }

  const { databaseDraft, state } = readViewSnapshot(contentId, viewId);
  const sourceFieldIds = getVisibleSourceFieldIds(
    databaseDraft.fields,
    manager.database.fieldsById
  );
  const targetEntryIds = getTargetEntryIds(
    databaseDraft.entries,
    manager.database,
    fieldId,
    scope,
    adapter
  );
  const preparedEntries = new Map<string, EntryPreparation>();
  const resolveJiraIssueDetails = createJiraIssueDetailsResolver(manager.database, state);
  const resolveConfluencePageConnection = createConfluencePageConnectionResolver(state);
  const entryFieldValuesSnapshot = createDatabaseEntryFieldValuesSnapshot(
    manager.database,
    {
      entryIds: targetEntryIds,
      fieldIds: databaseDraft.fields,
      resolveJiraIssueDetails,
      resolveConfluencePageConnection
    }
  );
  const configurationHash = await createFingerprintHash(configurationInput);

  await Promise.all(
    targetEntryIds.map(async (entryId) => {
      const entry = manager.database.entriesById[entryId];
      if (!entry) {
        return;
      }
      const sourceInput = createSourceInputForEntry(
        entryId,
        sourceFieldIds,
        entryFieldValuesSnapshot
      );
      const targetValue = entry.getValue(fieldId);
      preparedEntries.set(entryId, {
        fingerprint: {
          fingerprintVersion: 2,
          sourceValueHash: await createFingerprintHash(sourceInput),
          configurationHash
        },
        targetValue: serializeTargetValue(targetValue)
      });
    })
  );

  const eligibleEntryIds = targetEntryIds.filter((entryId) => {
    const prepared = preparedEntries.get(entryId);
    if (eligibility === 'all-targets') {
      return prepared !== undefined;
    }

    const targetValue = manager.database.entriesById[entryId]?.getValue(fieldId);
    if (!prepared || adapter.isEmptyValue(targetValue)) {
      return prepared !== undefined;
    }

    const fingerprint = adapter.getGenerationFingerprint(targetValue);
    return (
      fingerprint?.fingerprintVersion !== 2 ||
      fingerprint.sourceValueHash !== prepared.fingerprint.sourceValueHash ||
      fingerprint.configurationHash !== prepared.fingerprint.configurationHash
    );
  });
  const eligibleEntries = new Set(eligibleEntryIds);

  return {
    eligibleEntryIds,
    requestContext: entryFieldValuesSnapshot,
    generationWriteContext: {
      getFingerprint: (entryId) => preparedEntries.get(entryId)?.fingerprint,
      shouldCommit: (entryId) => {
        const prepared = preparedEntries.get(entryId);
        const currentManager = collabStateManager.getCollabManager(contentId);
        const entry = currentManager?.database.entriesById[entryId];
        const currentTargetField = currentManager?.database.fieldsById[fieldId];
        if (
          !prepared ||
          !entry ||
          !currentTargetField ||
          !adapter.isTargetField(currentTargetField) ||
          !eligibleEntries.has(entryId)
        ) {
          return false;
        }

        // The fingerprint describes the request snapshot. If source values change while
        // generation is running, the result can land and the next refresh detects staleness.
        return serializeTargetValue(entry.getValue(fieldId)) === prepared.targetValue;
      }
    }
  };
}

export const prepareAiFieldRefresh = (
  contentId: string,
  fieldId: string,
  scope: ManualAiFieldRefreshScope,
  { viewId, config }: { viewId: string; config: AiSummaryFieldConfig }
): Promise<PreparedAiFieldRefresh> =>
  prepareAiFieldRun(
    contentId,
    fieldId,
    scope,
    viewId,
    createAiSummaryConfigurationInput(config),
    aiSummaryFieldRefreshPreparationAdapter,
    'empty-or-stale'
  );

export const prepareAiFieldGeneration = (
  contentId: string,
  fieldId: string,
  scope: ManualAiFieldRefreshScope,
  { viewId, config }: { viewId: string; config: AiSummaryFieldConfig }
): Promise<PreparedAiFieldRefresh> =>
  prepareAiFieldRun(
    contentId,
    fieldId,
    scope,
    viewId,
    createAiSummaryConfigurationInput(config),
    aiSummaryFieldRefreshPreparationAdapter,
    'all-targets'
  );

export const hasNoEmptyAiSummaryEntries = ({
  contentId,
  fieldId,
  viewId
}: {
  contentId: string;
  fieldId: string;
  viewId: string;
}): boolean => {
  const manager = collabStateManager.getCollabManager(contentId);
  if (!manager) {
    return false;
  }

  const { databaseDraft } = readViewSnapshot(contentId, viewId);
  return (
    getTargetEntryIds(
      databaseDraft.entries,
      manager.database,
      fieldId,
      { kind: 'empty-target-field-entries' },
      aiSummaryFieldRefreshPreparationAdapter
    ).length === 0
  );
};
